import React, { useState } from 'react';
import { Upload, Button, Table, message, Modal, Card, Alert, Tag, Steps, Space, Progress } from 'antd';
import { UploadOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

const ExcelImportModal = ({ visible, onClose, onImportSuccess, categories, professions }) => {
    const [fileData, setFileData] = useState([]);
    const [validData, setValidData] = useState([]);
    const [errors, setErrors] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [uploading, setUploading] = useState(false);

    // Fonction pour télécharger le modèle Excel
    const downloadTemplate = () => {
        const template = [
            {
                // Souscripteur
                'Type Personne': 'PHYSIQUE',
                'Nom / Dénomination Sociale': 'KOUASSI',
                'Prénom(s)': 'Jean Pierre',
                'Email': 'jean@example.com',
                'Téléphone': '90123456',
                'Adresse': 'Lomé, Tokoin',
                'Sexe': 'MASCULIN',
                'Profession': 'COMMERCANT',
                'Type Pièce': 'CNI',
                'Numéro Pièce': 'TG123456',
                'Lieu Délivrance': 'Lomé',
                'Date Délivrance': '01/01/2020',
                'Date Naissance': '15/06/1985',
                'N° Registre Commerce': '',
                'NIF': '',
                'Exonéré Taxe': 'NON',

                // Engin
                'Catégorie': 'AUTO',
                'Type Série': 'TG',
                'Matricule': '1234',
                'Numéro Série': 'AB',
                'Marque': 'TOYOTA',
                'Énergie': 'DIESEL',
                'Usage': 'PRIVE',
                'Volume/Puissance': '2500',
                'Nombre Places': '5',
                'Genre': '',
                'N° Moteur': 'MOT123456',
                'Type': 'BERLINE',
                'N° Châssis': 'CHAS123456789',
                'Carrosserie': '',
                'Valeur Vénale': '5000000',
                'Valeur Déclarée': '5000000',
                'Valeur à Neuf': '8000000',
                'Année Mise en Circulation': '2020',
                'Garanties Supplémentaires': '',

                // Conducteur
                'Conducteur Différent': 'NON',
                'Conducteur - Nom': '',
                'Conducteur - Prénom': '',
                'Conducteur - Téléphone': '',
                'Conducteur - Adresse': '',
                'Conducteur - Profession': '',

                // Garanties
                'Date Effet': '01/12/2024',
                'Fractionnement (mois)': '12',
                'Taux Commission': '15'
            }
        ];

        const ws = XLSX.utils.json_to_sheet(template);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Modèle Import');
        XLSX.writeFile(wb, 'modele_import_contrats.xlsx');
        message.success('Modèle téléchargé avec succès!');
    };

    // Parser une date Excel ou texte
    const parseDate = (dateValue) => {
        if (!dateValue) return null;

        // Si c'est un nombre Excel (date seriale)
        if (typeof dateValue === 'number') {
            const date = XLSX.SSF.parse_date_code(dateValue);
            return dayjs(`${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`);
        }

        // Si c'est une chaîne au format JJ/MM/AAAA
        if (typeof dateValue === 'string') {
            const parts = dateValue.split('/');
            if (parts.length === 3) {
                return dayjs(`${parts[2]}-${parts[1]}-${parts[0]}`);
            }
        }

        return null;
    };

    // Valider et transformer les données
    const validateAndTransformData = (rawData) => {
        const valid = [];
        const invalid = [];

        rawData.forEach((row, index) => {
            const rowErrors = [];
            const lineNumber = index + 2; // +2 car ligne 1 = headers, index commence à 0

            try {
                // Validation Souscripteur
                if (!row['Type Personne'] || !['PHYSIQUE', 'MORALE'].includes(row['Type Personne'])) {
                    rowErrors.push('Type Personne invalide');
                }
                if (!row['Nom / Dénomination Sociale']) {
                    rowErrors.push('Nom/Dénomination Sociale requis');
                }
                if (!row['Téléphone'] || !/^(22|90|91|92|93|96|98|99|70|71|79)\d{6}$/.test(row['Téléphone'])) {
                    rowErrors.push('Téléphone invalide');
                }
                if (!row['Adresse']) {
                    rowErrors.push('Adresse requise');
                }

                // Validation Engin
                if (!row['Catégorie']) {
                    rowErrors.push('Catégorie requise');
                }
                if (!row['Type Série'] || !['TG', 'Autre'].includes(row['Type Série'])) {
                    rowErrors.push('Type Série invalide');
                }
                if (!row['Matricule']) {
                    rowErrors.push('Matricule requis');
                }
                if (row['Type Série'] === 'TG' && !row['Numéro Série']) {
                    rowErrors.push('Numéro Série requis pour type TG');
                }
                if (!row['Marque']) {
                    rowErrors.push('Marque requise');
                }
                if (!row['Énergie'] || !['ESSENCE', 'DIESEL', 'ELECTRIQUE'].includes(row['Énergie'])) {
                    rowErrors.push('Énergie invalide');
                }
                if (!row['Usage'] || !['PRIVE', 'TAXI'].includes(row['Usage'])) {
                    rowErrors.push('Usage invalide');
                }
                if (!row['N° Châssis']) {
                    rowErrors.push('N° Châssis requis');
                }
                if (!row['Date Effet']) {
                    rowErrors.push('Date Effet requise');
                }

                if (rowErrors.length > 0) {
                    invalid.push({
                        line: lineNumber,
                        data: row,
                        errors: rowErrors
                    });
                } else {

                    const toUpperSafe = (value) => {
                        if (!value) return '';
                        return String(value).toUpperCase();
                    };

                    // Transformer les données
                    const transformed = {
                        souscripteur: {
                            typePersonne: row['Type Personne'],
                            nom: row['Nom / Dénomination Sociale'],
                            prenom: row['Prénom(s)'] || '',
                            email: row['Email'] || '',
                            contact: [row['Téléphone']],
                            adresse: row['Adresse'],
                            genre: row['Sexe'] || '',
                            profession: row['Profession'] || '',
                            typePiece: row['Type Pièce'] || null,
                            numIdentite: row['Numéro Pièce'] || '',
                            lieuDelivrance: row['Lieu Délivrance'] || '',
                            dateDelivrance: row['Date Délivrance'] || '',
                            dateNaissance: row['Date Naissance'] || '',
                            numeroRegistreCommerce: row['N° Registre Commerce'] || '',
                            nif: row['NIF'] || '',
                            exonereDeTaxe: row['Exonéré Taxe'] === 'OUI'
                        },
                        engin: {
                            categorie: row['Catégorie'],
                            typeSerie: row['Type Série'],
                            matricule: toUpperSafe(row['Matricule']),
                            numeroSerie: row['Numéro Série'] ? toUpperSafe(row['Numéro Série']) : '',
                            immatriculation: row['Type Série'] === 'TG'
                                ? toUpperSafe(`${row['Type Série']} ${row['Matricule']} ${row['Numéro Série']}`)
                                : toUpperSafe(row['Matricule']),
                            marque: row['Marque'],
                            energie: row['Énergie'],
                            usage: row['Usage'],
                            volume: Number(row['Volume/Puissance']),
                            puissance: Number(row['Volume/Puissance']),
                            nombrePlace: Number(row['Nombre Places']),
                            genre: row['Genre'] || '',
                            moteur: toUpperSafe(row['N° Moteur']),
                            type: toUpperSafe(row['Type']),
                            numeroChassis: toUpperSafe(row['N° Châssis']),
                            carosserie: row['Carrosserie'] || '',
                            valeurVenale: Number(row['Valeur Vénale']),
                            valeurDeclaree: Number(row['Valeur Déclarée']),
                            valeurANeuf: Number(row['Valeur à Neuf']),
                            dateMiseEnCirculation: parseDate(row['Année Mise en Circulation']),
                            garantiesSupplementaires: row['Garanties Supplémentaires']
                                ? row['Garanties Supplémentaires'].split(';').filter(Boolean)
                                : [],
                            useSouscripteur: row['Conducteur Différent'] !== 'OUI',
                            personne: row['Conducteur Différent'] === 'OUI' ? {
                                nom: row['Conducteur - Nom'] || '',
                                prenom: row['Conducteur - Prénom'] || '',
                                telephone: row['Conducteur - Téléphone'] || '',
                                adresse: row['Conducteur - Adresse'] || '',
                                profession: row['Conducteur - Profession'] || '',
                                contact: row['Conducteur - Téléphone'] ? [row['Conducteur - Téléphone']] : []
                            } : {}
                        },
                        dateEffet: parseDate(row['Date Effet']),
                        fractionnement: row['Fractionnement (mois)'] ? Number(row['Fractionnement (mois)']) : null,
                        tauxCommission: row['Taux Commission'] ? Number(row['Taux Commission']) : 0,
                        lineNumber
                    };

                    valid.push(transformed);
                }
            } catch (error) {
                invalid.push({
                    line: lineNumber,
                    data: row,
                    errors: [`Erreur de traitement: ${error.message}`]
                });
            }
        });

        return { valid, invalid };
    };

    // Gérer l'upload du fichier
    const handleFileUpload = (file) => {
        setUploading(true);
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                if (jsonData.length === 0) {
                    message.error('Le fichier est vide');
                    setUploading(false);
                    return;
                }

                setFileData(jsonData);
                const { valid, invalid } = validateAndTransformData(jsonData);
                setValidData(valid);
                setErrors(invalid);
                setCurrentStep(1);

                if (invalid.length === 0) {
                    message.success(`${valid.length} contrat(s) validé(s) avec succès`);
                } else {
                    message.warning(`${valid.length} valide(s), ${invalid.length} erreur(s)`);
                }
            } catch (error) {
                message.error('Erreur lors de la lecture du fichier');
                console.error(error);
            } finally {
                setUploading(false);
            }
        };

        reader.readAsArrayBuffer(file);
        return false; // Empêcher l'upload automatique
    };

    // Colonnes du tableau d'erreurs
    const errorColumns = [
        {
            title: 'Ligne',
            dataIndex: 'line',
            key: 'line',
            width: 80,
            render: (text) => <Tag color="red">Ligne {text}</Tag>
        },
        {
            title: 'Erreurs',
            dataIndex: 'errors',
            key: 'errors',
            render: (errors) => (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {errors.map((err, idx) => (
                        <li key={idx} style={{ color: '#ff4d4f' }}>{err}</li>
                    ))}
                </ul>
            )
        }
    ];

    // Colonnes du tableau de données valides
    const validColumns = [
        {
            title: 'Ligne',
            dataIndex: 'lineNumber',
            key: 'lineNumber',
            width: 80,
            render: (text) => <Tag color="green">Ligne {text}</Tag>
        },
        {
            title: 'Souscripteur',
            key: 'souscripteur',
            render: (_, record) => (
                <div>
                    <strong>{record.souscripteur.nom}</strong>
                    {record.souscripteur.prenom && ` ${record.souscripteur.prenom}`}
                    <br />
                    <small>{record.souscripteur.contact[0]}</small>
                </div>
            )
        },
        {
            title: 'Engin',
            key: 'engin',
            render: (_, record) => (
                <div>
                    <strong>{record.engin.immatriculation}</strong>
                    <br />
                    <small>{record.engin.marque} - {record.engin.categorie}</small>
                </div>
            )
        },
        {
            title: 'Date Effet',
            dataIndex: 'dateEffet',
            key: 'dateEffet',
            render: (date) => date ? date.format('DD/MM/YYYY') : '-'
        }
    ];

    // Importer les données validées
    const handleImport = () => {
        if (validData.length === 0) {
            message.error('Aucune donnée valide à importer');
            return;
        }

        onImportSuccess(validData);
        message.success(`${validData.length} contrat(s) importé(s) avec succès`);
        handleClose();
    };

    const handleClose = () => {
        setFileData([]);
        setValidData([]);
        setErrors([]);
        setCurrentStep(0);
        onClose();
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileExcelOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                    <span>Import de contrats depuis Excel</span>
                </div>
            }
            open={visible}
            onCancel={handleClose}
            width={1200}
            footer={null}
        >
            <Steps
                current={currentStep}
                items={[
                    { title: 'Upload', icon: <UploadOutlined /> },
                    { title: 'Validation', icon: currentStep > 0 ? <CheckCircleOutlined /> : null },
                    { title: 'Import', icon: currentStep > 1 ? <CheckCircleOutlined /> : null }
                ]}
                style={{ marginBottom: 24 }}
            />

            {currentStep === 0 && (
                <Card>
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        <Alert
                            message="Téléchargez d'abord le modèle Excel"
                            description="Le modèle contient tous les champs nécessaires et des exemples de données"
                            type="info"
                            showIcon
                        />

                        <Button
                            icon={<DownloadOutlined />}
                            onClick={downloadTemplate}
                            type="primary"
                            size="large"
                            block
                        >
                            Télécharger le modèle Excel
                        </Button>

                        <Upload
                            accept=".xlsx,.xls"
                            beforeUpload={handleFileUpload}
                            showUploadList={false}
                            disabled={uploading}
                        >
                            <Button
                                icon={<UploadOutlined />}
                                size="large"
                                block
                                loading={uploading}
                            >
                                {uploading ? 'Chargement...' : 'Charger un fichier Excel'}
                            </Button>
                        </Upload>
                    </Space>
                </Card>
            )}

            {currentStep === 1 && (
                <div>
                    <Card style={{ marginBottom: 16 }}>
                        <Space size="large">
                            <div>
                                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
                                <span style={{ marginLeft: 8, fontSize: 18, fontWeight: 'bold' }}>
                                    {validData.length} valide(s)
                                </span>
                            </div>
                            {errors.length > 0 && (
                                <div>
                                    <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 24 }} />
                                    <span style={{ marginLeft: 8, fontSize: 18, fontWeight: 'bold' }}>
                                        {errors.length} erreur(s)
                                    </span>
                                </div>
                            )}
                        </Space>
                    </Card>

                    {errors.length > 0 && (
                        <Card title="Erreurs détectées" style={{ marginBottom: 16 }}>
                            <Alert
                                message="Certaines lignes contiennent des erreurs"
                                description="Corrigez les erreurs dans votre fichier Excel et réimportez-le"
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                            <Table
                                columns={errorColumns}
                                dataSource={errors}
                                pagination={{ pageSize: 5 }}
                                size="small"
                                rowKey="line"
                            />
                        </Card>
                    )}

                    {validData.length > 0 && (
                        <Card title="Données valides prêtes à importer">
                            <Table
                                columns={validColumns}
                                dataSource={validData}
                                pagination={{ pageSize: 10 }}
                                size="small"
                                rowKey="lineNumber"
                            />
                        </Card>
                    )}

                    <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={() => setCurrentStep(0)}>
                            Retour
                        </Button>
                        <Space>
                            <Button onClick={handleClose}>
                                Annuler
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleImport}
                                disabled={validData.length === 0}
                                icon={<CheckCircleOutlined />}
                            >
                                Importer {validData.length} contrat(s)
                            </Button>
                        </Space>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ExcelImportModal;